import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    Shield,
    Code2,
    Database,
    Zap,
    CheckCircle,
    Users,
    Globe,
    Lock,
    ArrowRight,
    BowArrow
} from "lucide-react";


export default function Django() {

    const navigate = useNavigate();

    const features = [
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Sécurité Renforcée",
            description: "Protection CSRF, XSS, SQL injection et gestion sécurisée des mots de passe par défaut"
        },
        {
            icon: <Database className="w-6 h-6" />,
            title: "ORM Puissant",
            description: "Abstraction de base de données avec migrations automatiques et requêtes optimisées"
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Admin Auto-généré",
            description: "Interface d'administration professionnelle créée automatiquement"
        },
        {
            icon: <Zap className="w-6 h-6" />,
            title: "Batteries Included",
            description: "Authentification, sessions, cache, internationalisation inclus"
        }
    ];

    const useCases = [
        "Applications web complexes avec logique métier avancée",
        "Plateformes SaaS multi-tenants",
        "API RESTful et GraphQL robustes",
        "Systèmes de gestion de contenu (CMS)",
        "Applications data-driven avec analyses complexes"
    ];

    const whyWeUse = [
        {
            title: "Framework Complet",
            description: "Tout ce dont vous avez besoin pour construire une application professionnelle"
        },
        {
            title: "Scalabilité Éprouvée",
            description: "Utilisé par Instagram, Pinterest, NASA et des milliers d'entreprises"
        },
        {
            title: "Communauté Python",
            description: "Écosystème riche avec des bibliothèques pour data science, ML et plus"
        }
    ];

    return (
        <div className="space-y-24">
            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/30 to-green-900/30 backdrop-blur-xl border border-emerald-500/30 p-12 md:p-16"
            >
                {/* Effet de fond animé */}
                <motion.div
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3]
                    }}
                    transition={{ duration: 8, repeat: Infinity }}
                    className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"
                />

                <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring" }}
                            className="inline-flex items-center gap-3 px-4 py-2 bg-emerald-500/20 rounded-full mb-6 border border-emerald-400/30"
                        >
                            <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse" />
                            <span className="text-emerald-300 font-semibold text-sm">Framework Python #1</span>
                        </motion.div>

                        <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            Django
                        </h2>

                        <p className="text-xl md:text-2xl text-emerald-300 mb-6 font-semibold">
                            The Web Framework for Perfectionists with Deadlines
                        </p>

                        <p className="text-gray-300 text-md md:text-lg leading-relaxed mb-8">
                            Django est un framework Python de haut niveau qui encourage le développement rapide
                            et une conception propre et pragmatique. Construit par des développeurs expérimentés,
                            il prend en charge une grande partie des tracas du développement Web.
                        </p>

                        <div className="flex flex-wrap gap-3">
                            {["MVT Pattern", "ORM", "Admin Interface", "Security"].map((tag, i) => (
                                <motion.span
                                    key={i}
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.5 + i * 0.1 }}
                                    className="px-4 py-2 bg-emerald-500/10 border border-emerald-400/20 rounded-full text-emerald-300 text-[12px] md:text-sm font-medium"
                                >
                                    {tag}
                                </motion.span>
                            ))}
                        </div>
                    </div>

                    {/* Logo Django animé */}
                    <motion.div
                        className="flex items-center justify-center"
                    >
                        <div className="relative w-64 h-64">
                            {/* Hexagone rotatif */}
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                className="absolute inset-0 flex items-center justify-center"
                            >
                                <svg width="200" height="200" viewBox="0 0 100 100">
                                    <motion.polygon
                                        points="50,5 95,27.5 95,72.5 50,95 5,72.5 5,27.5"
                                        fill="none"
                                        stroke="#10B981"
                                        strokeWidth="2"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                    />
                                </svg>
                            </motion.div>

                            {/* Centre pulsant */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <motion.div
                                    className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl shadow-lg shadow-emerald-500/50 flex items-center justify-center"
                                    animate={{
                                        scale: [1, 1.1, 1],
                                        rotate: [0, 5, -5, 0],
                                        boxShadow: [
                                            '0 0 20px rgba(16, 185, 129, 0.5)',
                                            '0 0 40px rgba(5, 150, 105, 0.7)',
                                            '0 0 20px rgba(16, 185, 129, 0.5)'
                                        ]
                                    }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    <span className="text-white font-bold text-3xl">D</span>
                                </motion.div>
                            </div>

                            {/* Particules vertes */}
                            {[...Array(6)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-3 h-3 bg-emerald-400/60 rounded-full"
                                    style={{
                                        top: `${50 + 35 * Math.sin(i * Math.PI / 3)}%`,
                                        left: `${50 + 35 * Math.cos(i * Math.PI / 3)}%`,
                                    }}
                                    animate={{
                                        scale: [1, 1.5, 1],
                                        opacity: [0.6, 1, 0.6]
                                    }}
                                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                                />
                            ))}
                        </div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Points Forts */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                <h3 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-12 text-center">
                    Les Forces de<span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-emerald-600 bg-clip-text text-transparent px-2 sm:px-4">Django</span>
                </h3>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                            whileHover={{
                                scale: 1.05,
                                y: -5,
                                transition: { type: "spring", stiffness: 400 }
                            }}
                            className="bg-gradient-to-br from-emerald-900/20 to-green-900/20 backdrop-blur-xl rounded-2xl p-6 border border-emerald-500/20 hover:border-emerald-400/40 transition-all duration-300 group"
                        >
                            <motion.div
                                whileHover={{ scale: 1.1, rotate: 5 }}
                                className="w-14 h-14 bg-gradient-to-br from-emerald-500/30 to-green-500/30 rounded-xl flex items-center justify-center mb-4 group-hover:shadow-xl group-hover:shadow-emerald-500/30 transition-all duration-300"
                            >
                                <div className="text-emerald-400">
                                    {feature.icon}
                                </div>
                            </motion.div>

                            <h4 className="text-xl font-bold text-white mb-2">
                                {feature.title}
                            </h4>
                            <p className="text-gray-300">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Cas d'Usage */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="grid lg:grid-cols-2 gap-12"
            >
                <div>
                    <h3 className="text-3xl font-bold text-white mb-6">
                        Applications Idéales pour Django
                    </h3>
                    <div className="space-y-4">
                        {useCases.map((useCase, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.6 + index * 0.1 }}
                                className="flex items-start gap-3 group"
                            >
                                <motion.div
                                    whileHover={{ scale: 1.2, rotate: 90 }}
                                    className="mt-1"
                                >
                                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                                </motion.div>
                                <p className="text-gray-300 group-hover:text-white transition-colors">
                                    {useCase}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <div>
                    <h3 className="text-3xl font-bold text-white mb-6">
                        Notre Maîtrise de Django
                    </h3>
                    <div className="space-y-6">
                        {whyWeUse.map((reason, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.7 + index * 0.1 }}
                                whileHover={{ x: 10 }}
                                className="bg-gradient-to-r from-emerald-500/10 to-transparent backdrop-blur-sm rounded-xl p-6 border-l-4 border-emerald-500"
                            >
                                <h4 className="text-xl font-bold text-emerald-300 mb-2">
                                    {reason.title}
                                </h4>
                                <p className="text-gray-300">
                                    {reason.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Stack Complémentaire */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bg-gradient-to-br from-emerald-900/20 to-green-900/20 backdrop-blur-xl rounded-3xl p-12 border border-emerald-500/20"
            >
                <h3 className="text-3xl font-bold text-white mb-8 text-center">
                    Notre Écosystème Django
                </h3>

                <div className="grid md:grid-cols-3 gap-6">
                    {[
                        { name: "Django REST", desc: "API RESTful puissantes", color: "from-emerald-600 to-emerald-800" },
                        { name: "Celery", desc: "Tâches asynchrones", color: "from-green-600 to-green-800" },
                        { name: "PostgreSQL", desc: "Base de données robuste", color: "from-blue-600 to-blue-800" },
                        { name: "Redis", desc: "Cache & messaging", color: "from-red-600 to-red-800" },
                        { name: "Docker", desc: "Conteneurisation", color: "from-cyan-600 to-cyan-800" },
                        { name: "Pytest", desc: "Tests automatisés", color: "from-yellow-600 to-yellow-800" }
                    ].map((tool, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.9 + index * 0.05 }}
                            whileHover={{ scale: 1.05 }}
                            className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-emerald-500/30 transition-all duration-300"
                        >
                            <div className={`h-2 w-16 bg-gradient-to-r ${tool.color} rounded-full mb-4`} />
                            <h4 className="text-lg font-bold text-white mb-2">{tool.name}</h4>
                            <p className="text-gray-400 text-sm">{tool.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* CTA */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="text-center flex flex-col items-center justify-center bg-gradient-to-r from-emerald-900/30 to-green-900/30 backdrop-blur-xl rounded-3xl p-12 border border-emerald-500/20"
            >
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Construisons Votre Application Django
                </h3>
                <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                    Profitez de notre expertise Django pour un développement rapide et sécurisé
                </p>
                <motion.button
                    onClick={() => navigate("/Contact")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-[250px] sm:w-[320px] cursor-pointer px-6 py-4 sm:px-8 sm:py-5  text-center text-[14px] sm:text-lg font-medium flex items-center gap-1 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold rounded-xl shadow-xl hover:shadow-2xl hover:shadow-emerald-500/50 transition-all duration-300 cursor-pointer"
                >
                    <span>Démarrer un projet Django</span>
                    <BowArrow className="w-5 h-5 md:w-6 md:h-6" />
                </motion.button>
            </motion.div>
        </div>
    );
}
